/* ==========================================================================
   Kankatala — SE Wishlist Engine glue (this theme only).
   The app (theme app embed "Wishlist Engine") does the actual add/remove and
   fills every empty `.wishlist-engine` (snippets/kankatala-wishlist-button.liquid).
   This file only:
   - asks the app to pick up hearts added after page load (product rows,
     Section Rendering, the cart drawer) via its public window.WishEngineinit();
   - mirrors the app's data-added into aria-pressed + our aria-label;
   - keeps duplicate hearts of the same variant in sync (the app only flips
     the one that was clicked);
   - makes the hearts keyboard operable (Enter / Space);
   - refreshes the Prestige cart after "Add to cart" on the wishlist page
     (the page's own layout/prices: assets/kankatala-wishlist-page.js);
   - cart bin: "Remove from cart? Remove / Move to wishlist" confirm, like the
     old Dawn theme. The move saves through the line's hidden app heart, then
     removes the saree AND its add-ons through the existing bin handler
     (initKankatalaTailoringCartSync in kankatala-custom.js), so no orphan add-on.
   No app settings are read or changed here.
   ========================================================================== */

(function () {
  'use strict';

  if (window.kankatalaWishlistInit) return;
  window.kankatalaWishlistInit = true;

  var LABEL_ADD = 'Add to wishlist';
  var LABEL_SAVED = 'Saved to wishlist — remove';
  var HEART_SELECTOR = '.kk-wishlist';

  /* ------------------------------------------------------------------------
     Late hearts → window.WishEngineinit()
     WishEngineinit binds a click listener to EVERY empty .wishlist-engine,
     even ones it already bound, so it is only called for hearts the app has
     never seen (no data-apply-event), and only once the app's own first pass
     has run — otherwise a heart could get two listeners (add + remove).
  ------------------------------------------------------------------------ */
  var appLoadedAt = 0;

  function appReady() {
    if (typeof window.WishEngineinit !== 'function') return false;
    if (document.querySelector('.wishlist-engine[data-apply-event]')) return true;
    // a page that had no hearts at load: give the app time for its first pass
    if (!appLoadedAt) appLoadedAt = Date.now();
    return Date.now() - appLoadedAt > 4000;
  }

  var scanTimer = null;

  function scheduleScan(attempt) {
    clearTimeout(scanTimer);
    scanTimer = setTimeout(function () {
      if (!document.querySelector(HEART_SELECTOR + ':empty:not([data-apply-event])')) return;
      if (!appReady()) {
        if ((attempt || 0) < 30) scheduleScan((attempt || 0) + 1);
        return;
      }
      try {
        window.WishEngineinit();
      } catch (error) {
        /* app not fully loaded yet */
      }
    }, attempt ? 500 : 150);
  }

  /* ------------------------------------------------------------------------
     State → ARIA, and duplicate hearts in sync
  ------------------------------------------------------------------------ */
  function syncAria(heart) {
    var added = heart.getAttribute('data-added') === 'true';
    var pressed = added ? 'true' : 'false';
    var label = added ? LABEL_SAVED : LABEL_ADD;
    if (heart.getAttribute('aria-pressed') !== pressed) heart.setAttribute('aria-pressed', pressed);
    if (heart.getAttribute('aria-label') !== label) heart.setAttribute('aria-label', label);
  }

  function syncDuplicates(heart) {
    var added = heart.getAttribute('data-added');
    if (!heart.innerHTML) return;
    document.querySelectorAll(HEART_SELECTOR + '[data-product_id="' + heart.dataset.product_id + '"]').forEach(function (other) {
      if (other === heart) return;
      if (other.dataset.variant_id !== heart.dataset.variant_id) return;
      if (!other.dataset.applyEvent) return; // the app hasn't bound it yet; its own status check will set it
      if (other.getAttribute('data-added') === added) return;
      other.setAttribute('data-added', added);
      other.innerHTML = heart.innerHTML;
    });
  }

  function onMutations(records) {
    var added = false;
    records.forEach(function (record) {
      if (record.type === 'attributes') {
        var target = record.target;
        if (!target.classList || !target.classList.contains('kk-wishlist')) return;
        syncAria(target);
        if (record.attributeName === 'data-added') syncDuplicates(target);
      } else if (record.addedNodes.length) {
        added = true;
      }
    });
    if (added) scheduleScan();
  }

  /* ------------------------------------------------------------------------
     Keyboard: the hearts are role="button" divs (the app needs the element)
  ------------------------------------------------------------------------ */
  function onKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    var heart = event.target.closest && event.target.closest(HEART_SELECTOR);
    if (!heart || heart !== event.target) return;
    event.preventDefault();
    heart.click();
  }

  /* ------------------------------------------------------------------------
     Cart bin → "Remove from cart?" confirm
  ------------------------------------------------------------------------ */
  var HEART_SVG = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M12 20.25s-8.25-4.6-8.25-10.4A4.35 4.35 0 0 1 12 7.6a4.35 4.35 0 0 1 8.25 2.25c0 5.8-8.25 10.4-8.25 10.4Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>';
  var CLOSE_SVG = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M5 5l14 14M19 5L5 19" stroke="currentColor" stroke-width="1.5"/></svg>';

  var openDialog = null;

  function closeConfirm(restoreFocusTo) {
    if (!openDialog) return;
    var dialog = openDialog;
    openDialog = null;
    if (dialog.open) dialog.close();
    dialog.remove();
    if (restoreFocusTo && document.contains(restoreFocusTo)) restoreFocusTo.focus();
  }

  function removeLine(link) {
    link.setAttribute('data-kk-wl-confirmed', 'true');
    link.click(); // handled by the bin handler in kankatala-custom.js
  }

  function waitForSaved(heart, timeout) {
    return new Promise(function (resolve) {
      var start = Date.now();
      (function check() {
        if (heart.getAttribute('data-added') === 'true') return resolve(true);
        if (Date.now() - start > timeout) return resolve(false);
        setTimeout(check, 100);
      })();
    });
  }

  function openConfirm(link, heart) {
    closeConfirm();

    var dialog = document.createElement('dialog');
    dialog.className = 'kk-wl-confirm';
    dialog.setAttribute('aria-labelledby', 'kk-wl-confirm-title');
    dialog.setAttribute('aria-describedby', 'kk-wl-confirm-text');
    dialog.innerHTML =
      '<button type="button" class="kk-wl-confirm__close" data-kk-wl-close aria-label="Close">' + CLOSE_SVG + '</button>' +
      '<span class="kk-wl-confirm__icon">' + HEART_SVG + '</span>' +
      '<h2 class="kk-wl-confirm__title" id="kk-wl-confirm-title">Remove from cart?</h2>' +
      '<p class="kk-wl-confirm__text" id="kk-wl-confirm-text">You can save it to your wishlist and buy it later.</p>' +
      '<div class="kk-wl-confirm__actions">' +
        '<button type="button" class="kk-wl-confirm__button" data-kk-wl-remove>Remove</button>' +
        '<button type="button" class="kk-wl-confirm__button kk-wl-confirm__button--primary" data-kk-wl-move autofocus>Move to wishlist</button>' +
      '</div>' +
      '<p class="kk-wl-confirm__error" role="alert" hidden></p>';

    // Inside the cart drawer, so Prestige's drawer focus trap treats it as "inside"
    // (a click outside the drawer would close it).
    (link.closest('cart-drawer') || document.body).appendChild(dialog);
    dialog.__kkLink = link;
    openDialog = dialog;

    var removeButton = dialog.querySelector('[data-kk-wl-remove]');
    var moveButton = dialog.querySelector('[data-kk-wl-move]');
    var error = dialog.querySelector('.kk-wl-confirm__error');

    dialog.addEventListener('click', function (event) {
      if (event.target === dialog || event.target.closest('[data-kk-wl-close]')) {
        closeConfirm(link);
      }
    });

    removeButton.addEventListener('click', function () {
      closeConfirm();
      removeLine(link);
    });

    moveButton.addEventListener('click', function () {
      if (moveButton.getAttribute('aria-busy') === 'true') return;
      moveButton.setAttribute('aria-busy', 'true');
      removeButton.disabled = true;
      error.hidden = true;

      var saved = heart.getAttribute('data-added') === 'true'
        ? Promise.resolve(true)
        : (heart.click(), waitForSaved(heart, 8000));

      saved.then(function (ok) {
        if (ok) {
          closeConfirm();
          removeLine(link);
          return;
        }
        moveButton.removeAttribute('aria-busy');
        removeButton.disabled = false;
        error.textContent = 'Sorry, we could not save it to your wishlist. Please try again.';
        error.hidden = false;
      });
    });

    dialog.showModal();
  }

  function onCartClick(event) {
    var link = event.target.closest && event.target.closest('[data-kankatala-remove-line]');
    if (!link) return;

    if (link.getAttribute('data-kk-wl-confirmed')) {
      link.removeAttribute('data-kk-wl-confirmed');
      return; // confirmed: let the bin handler remove it
    }

    var item = link.closest('.kankatala-cart-item');
    var heart = item && item.querySelector('.kk-wishlist--cart');
    if (!heart || !heart.dataset.applyEvent) return; // app not loaded: plain remove, as before
    if (link.getAttribute('aria-busy') === 'true') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    openConfirm(link, heart);
  }

  // Esc closes only the confirm, not the cart drawer underneath (window capture runs first).
  function onEscape(event) {
    if (event.key !== 'Escape' || !openDialog) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    var link = openDialog.__kkLink;
    closeConfirm(link);
  }

  /* ------------------------------------------------------------------------
     Wishlist page "Add to cart": refresh the Prestige cart (count + drawer).
     With this callback defined the app skips its Dawn-specific section refresh.
  ------------------------------------------------------------------------ */
  if (typeof window.wishlistEngineCallback !== 'function') {
    window.wishlistEngineCallback = function () {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    };
  }

  function init() {
    document.querySelectorAll(HEART_SELECTOR).forEach(syncAria);
    new MutationObserver(onMutations).observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-added', 'aria-label']
    });
    document.addEventListener('keydown', onKeydown);
    window.addEventListener('click', onCartClick, true);
    window.addEventListener('keydown', onEscape, true);
    document.addEventListener('shopify:section:load', function () {
      scheduleScan();
    });
    scheduleScan();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
